import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";

import { PageContainer, PageEmptyState, PageHeader } from "../components/AssetBreadCrumb";
import FilterBar from "../components/ReusableComponents/FilterBar";
import { Skeleton } from "../components/Skeleton";
import { ReactComponent as PlusIcon } from "../assets/icons/plus.svg";
import { ReactComponent as LayersTwoIcon } from "../assets/icons/layers-two-02.svg";
import ComposedForecastCard, {
  ComposedForecastCardSkeleton,
} from "../components/ComposedForecast/ComposedForecastCard";
import { setModalState, modalViewsConst } from "../store/modalSlice";
import { fetchComposedForecasts } from "../store/composedForecastSlice";
import {
  COMPOSED_FORECAST_SORT_OPTIONS,
  filterComposedForecasts,
  sortComposedForecasts,
} from "../utils/composedForecastUtils";
import { usePostHogCapture } from "../hooks/usePostHogCapture";
import { POSTHOG_EVENTS } from "../utils/posthogEvents";
import { canManageComposedForecast } from "../utils/composedForecastOwnership";
import { closeUrlParams, editUrlParams } from "../utils/detailLinks";
import { useOwnerCandidates } from "../hooks/useOwnerCandidates";
import { useLinkedModal } from "../hooks/useLinkedModal";
import type { ComposedForecast } from "../types/composedForecast";
import type { AppDispatch, RootState } from "../store";

// Status is a view of the same collection, so it takes chips rather than a second search filter
const STATUS_TABS = [
  { id: "all", label: "All" },
  { id: "active", label: "Active" },
  { id: "inactive", label: "Inactive" },
];

const matchesStatus = (forecast: ComposedForecast, tab: string) =>
  tab === "all" || (tab === "active" ? forecast.isActive : !forecast.isActive);

const ComposedForecastPage = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { capture } = usePostHogCapture();
  const { forecasts, loading, error } = useSelector((state: RootState) => state.composedForecasts);
  const cognitoGroups = useSelector((state: RootState) => (state.user as any)?.details?.groups);
  const ownerCandidates = useOwnerCandidates();

  const [activeTab, setActiveTab] = useState("all");
  const [searchValue, setSearchValue] = useState("");
  const [sortBy, setSortBy] = useState("name");
  // This visit's list has been read, so a shared link to a view not in it really is missing
  const [fetched, setFetched] = useState(false);

  useEffect(() => {
    // Only a successful read counts: a failed one leaves the list empty, and every link would look missing
    dispatch(fetchComposedForecasts()).then((action) => {
      if (fetchComposedForecasts.fulfilled.match(action)) setFetched(true);
    });
  }, [dispatch]);

  const handleCreate = () => {
    dispatch(
      setModalState({
        modalOpen: true,
        modalView: modalViewsConst.createComposedForecast,
        properties: { __modalSize: "lg" },
      })
    );
  };

  // The chart moved to its own modal — as an expanded table row it shared a cell with a scroller
  // The click only writes ?customView=<id>; the link effect below opens the modal, so a shared URL opens it the same way
  const navigate = useNavigate();
  const handleOpen = (forecast: ComposedForecast) => {
    capture(POSTHOG_EVENTS.COMPOSED_FORECAST_PREVIEW_EXPANDED, { forecast_name: forecast.name });
    editUrlParams(navigate, (params) => params.set("customView", forecast.id), false);
  };

  // Shared links: ?customView=<id> opens that view; useLinkedModal keeps link and modal in step
  const [searchParams] = useSearchParams();
  const linkedId = searchParams.get("customView");
  useLinkedModal<ComposedForecast>({
    linkKey: linkedId,
    ready: fetched,
    find: () => (linkedId ? (forecasts as Record<string, ComposedForecast>)[linkedId] ?? null : null),
    open: (forecast) =>
      dispatch(
        setModalState({
          modalOpen: true,
          modalView: modalViewsConst.composedForecastDetail,
          properties: { __modalSize: "xl", forecast },
        })
      ),
    isShowing: (modal, id) =>
      modal.modalOpen && modal.modalView === modalViewsConst.composedForecastDetail && modal.properties?.forecast?.id === id,
    clearLink: () => closeUrlParams(navigate, (params) => params.delete("customView"), "all"),
    notFound: {
      header: "Custom view not found",
      details: "This link points to a view that was deleted or that you don't have access to.",
    },
  });

  const handleEdit = (forecast: ComposedForecast) => {
    dispatch(
      setModalState({
        modalOpen: true,
        modalView: modalViewsConst.editComposedForecast,
        properties: { __modalSize: "lg", forecast },
      })
    );
  };

  // Admin-only for now; the card leaves the button out when this is undefined
  const isAdmin = !!cognitoGroups?.includes("Admin");
  const handleCopy = (forecast: ComposedForecast) => {
    dispatch(
      setModalState({
        modalOpen: true,
        modalView: modalViewsConst.copyComposedForecast,
        properties: { __modalSize: "sm", forecast },
      })
    );
  };

  const allForecasts = Object.values(forecasts) as ComposedForecast[];
  const currentForecasts = allForecasts.filter((forecast) => matchesStatus(forecast, activeTab));
  const filteredForecasts = sortComposedForecasts(
    filterComposedForecasts(currentForecasts, searchValue),
    sortBy
  );
  // The All view groups under Active/Inactive headers, the way Datasets sections its All view
  const activeForecasts = filteredForecasts.filter((forecast) => forecast.isActive);
  const inactiveForecasts = filteredForecasts.filter((forecast) => !forecast.isActive);

  const countFor = (tab: string) =>
    allForecasts.filter((forecast) => matchesStatus(forecast, tab)).length;

  // One renderer for both the grouped All view and the single-status tabs
  const renderForecasts = (list: ComposedForecast[]) => (
    <div className="eai-card-grid">
      {list.map((forecast) => (
        <ComposedForecastCard
          key={forecast.id}
          forecast={forecast}
          onOpen={handleOpen}
          onEdit={handleEdit}
          onCopy={isAdmin ? handleCopy : undefined}
          canManage={canManageComposedForecast(forecast, cognitoGroups, ownerCandidates)}
        />
      ))}
    </div>
  );

  // Home's eyebrow count: the tab's total, narrowing to "x of y" while a search is on
  const headerCount = currentForecasts.length;
  const shownCount = filteredForecasts.length;

  if (loading === "pending") {
    // Header, filter card and a card grid silhouette, so the page doesn't jump when data lands
    return (
      <PageContainer>
        <div role="status" aria-label="Loading custom views">
          <div className="eai-gap-header">
            <Skeleton className="h-3 w-20" />
            <Skeleton className="mt-2 h-7 w-64" radius="md" />
          </div>
          <Skeleton className="eai-gap-toolbar h-20 w-full" radius="md" />
          <div className="eai-card-grid">
            {Array.from({ length: 6 }).map((_, i) => (
              <ComposedForecastCardSkeleton key={i} />
            ))}
          </div>
        </div>
      </PageContainer>
    );
  }

  // Counts hide below sm and the track scrolls as a fallback, matching the Datasets picker
  // The panel copy is named by its own visible heading — two live groups answering to one aria-label is ambiguous to query
  const statusSeg = (labelledBy?: string) => (
    <div
      className="eai-seg max-w-full overflow-x-auto"
      role="group"
      aria-label={labelledBy ? undefined : "Forecast view status"}
      aria-labelledby={labelledBy}
    >
      {STATUS_TABS.map(({ id, label }) => {
        const active = activeTab === id;
        return (
          <button
            key={id}
            type="button"
            onClick={() => setActiveTab(id)}
            className={`eai-seg-item whitespace-nowrap ${active ? "eai-seg-item-active" : ""}`}
            aria-current={active ? "true" : undefined}
          >
            {label}
            <span className="eai-count-pill hidden sm:inline-flex">{countFor(id)}</span>
          </button>
        );
      })}
    </div>
  );

  return (
    <PageContainer>
      <PageHeader
        eyebrow={
          <>
            Custom Views
            <span className="eai-count-pill ml-2">
              {shownCount === headerCount ? headerCount : `${shownCount} of ${headerCount}`}
            </span>
          </>
        }
        title="My Custom Views"
        action={
          /* Label folds away under sm like the other list pages, so the pill needs its own name there */
          <button
            type="button"
            onClick={handleCreate}
            className="eai-btn-cta eai-btn-icon-mobile"
            aria-label="New Custom View"
            title="New Custom View"
          >
            <PlusIcon className="h-4 w-4 shrink-0" />
            <span className="hidden sm:inline">New Custom View</span>
          </button>
        }
      />

      {error && (
        <div
          role="alert"
          className="mb-5 rounded-eai-card border border-eai-danger-border bg-eai-danger-surface px-4 py-3 font-inter text-eai-body-sm text-eai-danger-text"
        >
          {error}
        </div>
      )}

      {/* Desktop only — on a phone the picker moves into the Filters panel so the page opens like Home */}
      <div className="mb-5 hidden sm:block">
        {statusSeg()}
      </div>

      {/* Reaches into the sources and linked assets too — the page had no search of its own at all */}
      <FilterBar
        sticky
        searchId="forecast-views-search"
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        searchPlaceholder="Search by name, source, or asset"
        sortBy={sortBy}
        onSortChange={setSortBy}
        sortOptions={COMPOSED_FORECAST_SORT_OPTIONS}
        onClear={() => setSearchValue("")}
        isFiltered={Boolean(searchValue)}
        // Status only folds into the panel below sm, so only there does Clear reset it
        onClearMobile={() => setActiveTab("all")}
        mobileControls={
          <div>
            <span id="forecast-views-status-label" className="eai-meta-label mb-1.5 block">
              Status
            </span>
            {statusSeg("forecast-views-status-label")}
          </div>
        }
        mobileActiveCount={activeTab === "all" ? 0 : 1}
      />

      {filteredForecasts.length === 0 ? (
        <PageEmptyState
          icon={<LayersTwoIcon className="h-6 w-6 [&_path]:stroke-current" />}
          description={
            searchValue
              ? `No custom views found matching "${searchValue}"`
              : activeTab === "all"
                ? "No custom views yet. Create one to combine your data sources."
                : `No ${activeTab} custom views.`
          }
          action={
            !searchValue &&
            activeTab === "all" && (
              <button type="button" onClick={handleCreate} className="eai-btn-cta">
                <PlusIcon className="h-4 w-4" />
                Create Your First Custom View
              </button>
            )
          }
        />
      ) : activeTab === "all" ? (
        <>
          {activeForecasts.length > 0 && (
            <>
              <h2 className="eai-section-label">Active</h2>
              {renderForecasts(activeForecasts)}
            </>
          )}
          {inactiveForecasts.length > 0 && (
            <>
              <h2 className={`eai-section-label ${activeForecasts.length > 0 ? "eai-gap-section" : ""}`}>
                Inactive
              </h2>
              {renderForecasts(inactiveForecasts)}
            </>
          )}
        </>
      ) : (
        renderForecasts(filteredForecasts)
      )}
    </PageContainer>
  );
};

// Open to everyone: AppSync lists only the views a user owns or shares an org with, and each card hides editing
// from anyone who can't manage it
export default ComposedForecastPage;
